import { useState } from 'react';
import { Bot } from 'lucide-react';
import { Button, Skeleton, toast } from '@clarix/ui';
import { ConfirmDialog, CopyButton } from '@clarix/ui-shared';
import {
  AI_CONNECTION_ACCESS_LABEL,
  useDisconnectAiConnection,
  useMyAiConnections,
  type AiConnection,
} from '@clarix/services-users';
import { AiConnectorHelp } from './AiConnectorHelp';

/** Formats an ISO date to a friendly "Jan 5, 2026" label. */
function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Profile → "Connected AI apps": the member's workspace MCP URL, the AI apps
 * holding a grant to act as them here, and how to connect a new one (spec C5, D).
 */
export function ConnectedAiAppsSection() {
  const mine = useMyAiConnections();

  if (mine.isLoading) return <Skeleton className="h-10 w-full rounded-md" />;

  if (mine.isError || !mine.data) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-xl">Couldn&apos;t load your connected AI apps.</p>
        <Button variant="outline" size="sm" onClick={() => void mine.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const { items, mcp_url, allow_ai_connections } = mine.data;

  return (
    <div className="space-y-4">
      {allow_ai_connections ? (
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm font-medium uppercase tracking-wide">
            Your workspace MCP URL
          </p>
          <div className="bg-surface flex items-start gap-2 rounded-md px-2.5 py-1.5">
            <code className="text-foreground min-w-0 flex-1 break-all font-mono text-lg">
              {mcp_url}
            </code>
            <CopyButton
              value={mcp_url}
              label="MCP URL"
              iconSize="h-4 w-4"
              toastOnCopy
              className="mt-0.5 h-7 w-7"
            />
          </div>
        </div>
      ) : (
        <p className="text-muted-foreground text-xl">
          An owner has turned off AI app connections for this workspace, so new AI apps can&apos;t
          connect.
        </p>
      )}

      <ConnectionList items={items} />

      {allow_ai_connections && (
        <details className="group">
          <summary className="text-primary cursor-pointer text-lg font-medium">
            How to connect an AI app
          </summary>
          <div className="mt-3">
            <AiConnectorHelp mcpUrl={mcp_url} />
          </div>
        </details>
      )}
    </div>
  );
}

function ConnectionList({ items }: { items: AiConnection[] }) {
  const disconnect = useDisconnectAiConnection();
  const [pending, setPending] = useState<AiConnection | null>(null);

  if (items.length === 0) {
    return <p className="text-muted-foreground text-xl">No AI apps connected yet.</p>;
  }

  const onConfirmDisconnect = () => {
    if (!pending) return;
    const grant = pending;
    disconnect.mutate(grant.id, {
      onSuccess: () => toast.success(`${grant.client_name} disconnected`),
      onError: () => toast.error(`Couldn't disconnect ${grant.client_name}. Please try again.`),
      onSettled: () => setPending(null),
    });
  };

  return (
    <>
      <ul className="divide-border divide-y">
        {items.map((grant) => {
          const nameId = `ai-connection-${grant.id}`;
          const busy = disconnect.isPending && disconnect.variables === grant.id;
          return (
            <li key={grant.id} aria-labelledby={nameId} className="flex items-center gap-3 py-2.5">
              <span className="bg-surface text-muted-foreground flex h-8 w-8 flex-none items-center justify-center rounded-md">
                <Bot className="h-4 w-4" strokeWidth={1.5} />
              </span>
              <div className="min-w-0 flex-1">
                <p id={nameId} className="text-foreground truncate text-xl font-medium">
                  {grant.client_name}
                </p>
                <p className="text-muted-foreground truncate text-lg">
                  <span>
                    {AI_CONNECTION_ACCESS_LABEL[grant.access] ??
                      AI_CONNECTION_ACCESS_LABEL.read_write}
                  </span>
                  {' · '}
                  <span>Connected {formatDate(grant.created_at)}</span>
                  {' · '}
                  <span>
                    {grant.last_used_at
                      ? `Last used ${formatDate(grant.last_used_at)}`
                      : 'Not used yet'}
                  </span>
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive flex-none"
                disabled={busy}
                onClick={() => setPending(grant)}
              >
                {busy ? 'Disconnecting…' : 'Disconnect'}
              </Button>
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={`Disconnect ${pending?.client_name ?? 'this app'}?`}
        description="It loses access to Clarix right away. To use it again you'll need to connect it and click Allow again. Nothing in your workspace is changed."
        cancelLabel="Keep connected"
        confirmLabel="Disconnect"
        loadingLabel="Disconnecting…"
        variant="destructive"
        loading={disconnect.isPending}
        onConfirm={onConfirmDisconnect}
      />
    </>
  );
}
